// OrgConsolePage.js — org owner console (v2 redesign, council-synthesized 2026-10-01)
// Structure (workflow order, not data-model order):
//   header (freshness) → org switcher → needs-attention rail → client trusts
//   (search + filter + incremental rendering) → activity → team (collapsible,
//   real member actions) → org settings row.
// Scales to many trusts/orgs: search + filters + "Show more" (24-card batch),
// derived rail always computed over ALL loaded trusts, switcher dropdown >3 orgs.
// Calls GET /api/orgs, /api/orgs/{org_id}/members, /api/orgs/{org_id}/trusts,
// /api/orgs/{org_id}/activity; PATCH /orgs/{id}/members/{member_id};
// POST /orgs/enter-trust/{trust_id} (audited workspace entry).
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import PageHeader from '@/components/PageHeader';
import { fetchWithAuth } from '@/utils/api';
import { showError, humanizeErrorCode } from '@/utils/errors';
import { isValidEmail } from '@/utils/formGuards';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Building2, RefreshCw, UserPlus, FileText, Users, ArrowUpRight,
  Activity, CalendarClock, FileSignature, Search, ChevronDown, Settings, Eye,
  MoreHorizontal, PauseCircle, PlayCircle, Copy, AlertTriangle,
} from 'lucide-react';

const fmtDate = (iso) => {
  try {
    // Date-only strings ("2026-10-13") must not shift through UTC — parse as local.
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  catch { return iso; }
};
const fmtRelative = (iso) => {
  try {
    const d = new Date(iso); const diff = Date.now() - d.getTime();
    if (diff < 0) return fmtDate(iso);
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return days === 1 ? 'yesterday' : `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

// Activity feed verbs → readable labels
// "quarterly_review" → "Quarterly Review" (calendar/queue fallback titles)
const humanizeTaskType = (key) =>
  (key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
// "2026-W43" → "Week of Oct 13" (ISO week from week key)
const weekLabel = (wk) => {
  const m = /^([0-9]{4})-W([0-9]{2})$/.exec(wk || '');
  if (!m) return wk;
  const d = new Date(Date.UTC(Number(m[1]), 0, 1 + (Number(m[2]) - 1) * 7));
  const dow = d.getUTCDay() || 7;
  if (dow <= 4) d.setUTCDate(d.getUTCDate() - dow + 1); else d.setUTCDate(d.getUTCDate() + 8 - dow);
  const md = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
  return `Week of ${md}`;
};
const fmtShort = (iso) => {
  // "2026-11-04" / ISO timestamp → "Nov 4" (no UTC shift for date-only strings)
  try {
    const day = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return String(iso || '');
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return String(iso || ''); }
};
const dueChip = (iso) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!iso) return null;
  const day = String(iso).slice(0, 10);
  if (day < today) return { text: `Overdue ${fmtShort(day)}`, cls: 'text-red-600 bg-red-50 border-red-200' };
  if (day === today) return { text: 'Today', cls: 'text-navy bg-gold/25 border-gold/50' };
  if (day === new Date(Date.now() + 864e5).toISOString().slice(0, 10)) return { text: 'Tomorrow', cls: 'text-navy bg-gold/15 border-gold/40' };
  return null;
};

const ACTION_LABELS = {
  // lifecycle events (schema-B, normalized backfeed — Jeff 2026-10-09)
  org_created: 'Created the organization',
  invite_sent: 'Invited a team member',
  invite_accepted: 'Joined the organization',
  grant_created: 'Granted account access',
  grant_revoked: 'Revoked account access',
  grant_expiring: 'Access expiring soon',
  // misc advisor actions seen in prod
  minutes_finalized: 'Finalized minutes',
  minutes_autosaved: 'Autosaved a minutes draft',
  minutes_created: 'Drafted minutes',
  minutes_updated: 'Updated minutes',
  meeting_recorded: 'Recorded a meeting',
  upload_vault_document: 'Uploaded a vault document',
  list_vault_documents: 'Viewed vault documents',
  asset_updated: 'Updated an asset record',
  beneficiary_updated: 'Updated a beneficiary',
  trustee_appointment_sent: 'Sent a trustee appointment',
  distribution_created: 'Recorded a distribution',
  distribution_approved: 'Approved a distribution',
  distribution_status_changed: 'Updated a distribution status',
  distribution_minutes_attached: 'Attached minutes to a distribution',
  distribution_notice_sent: 'Sent a distribution notice',
  admin_kit_generated: 'Generated the admin kit',
  successor_packet_sent: 'Sent a successor packet',
  trust_protector_appointment_sent: 'Sent a trust protector appointment',
  schedule_a_item_created: 'Added a Schedule A asset',
  schedule_a_item_updated: 'Updated a Schedule A asset',
  schedule_a_item_confirmed: 'Confirmed a Schedule A asset',
  schedule_a_item_disposed: 'Disposed of a Schedule A asset',
  schedule_a_pdf_exported: 'Exported the Schedule A PDF',
  grant_expiring_7d: 'Grant expiry notice sent',
};

const TRUST_PAGE_SIZE = 24;

// Short title for the member-action confirm dialog (its description carries the
// full prompt copied verbatim from the member menu).
const memberConfirmTitle = (cm) => {
  if (!cm) return 'Update member';
  if (cm.body?.role) return cm.body.role === 'admin' ? 'Make org admin' : 'Set role to member';
  if (cm.body?.status === 'suspended') return 'Suspend member';
  if (cm.body?.status === 'active') return 'Activate member';
  return 'Update member';
};

export default function OrgConsolePage() {
  const navigate = useNavigate();
  const { setSelectedTrust } = useAuth();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);            // page-level load failure (distinct from empty)
  const [lastUpdated, setLastUpdated] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [members, setMembers] = useState({});
  const [trustsByOrg, setTrustsByOrg] = useState({});
  const [activity, setActivity] = useState({});
  const [failedOrgs, setFailedOrgs] = useState({});  // per-org section failures
  // — Phase 1: oversight dashboard + review queue (advisor features) —
  const [overview, setOverview] = useState({});
  const [queue, setQueue] = useState(null);
  const [queueBusy, setQueueBusy] = useState(null);
  const [firmCal, setFirmCal] = useState(null);
  const [focusOrg, setFocusOrg] = useState(null);    // active org in switcher
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // trusts browser
  const [trustQuery, setTrustQuery] = useState('');
  const [trustLevel, setTrustLevel] = useState('all'); // all | preparer | viewer
  const [trustShown, setTrustShown] = useState(TRUST_PAGE_SIZE);
  // — Phase 0: server-backed search for orgs beyond client-aggregation scale (>24) —
  const SERVER_MODE_THRESHOLD = 48;
  const [searchParams, setSearchParams] = useSearchParams();
  const [serverTrusts, setServerTrusts] = useState(null);   // null = client mode
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [trustStatus, setTrustStatus] = useState('all');
  const [trustDeadline, setTrustDeadline] = useState('all');
  const [trustSort, setTrustSort] = useState('pending_desc');
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  // team
  const [teamOpen, setTeamOpen] = useState(false);   // collapsed by default (>8 shows collapsed)
  const [teamQuery, setTeamQuery] = useState('');
  const [menuMember, setMenuMember] = useState(null); // open row menu member_id
  // invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteOrg, setInviteOrg] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteErr, setInviteErr] = useState('');
  const [isOwner, setIsOwner] = useState(false);
  const [confirmMember, setConfirmMember] = useState(null); // parked member action {member, body, confirmLabel} — resumed by its AlertDialog
  const refreshBtn = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setFailedOrgs({});
    try {
      const res = await fetchWithAuth('/orgs');
      if (!res.ok) { setErr(new Error(`orgs ${res.status}`)); setOrgs([]); return; }
      const list = await res.json();
      setOrgs(list);
      setFocusOrg(prev => (list.find(o => o.org_id === prev?.org_id) || list[0] || null));
      const mem = {}; const trs = {}; const acts = {}; const failed = {};
      await Promise.all(list.map(async o => {
        try {
          const [mRes, tRes, aRes] = await Promise.all([
            fetchWithAuth(`/orgs/${o.org_id}/members`),
            fetchWithAuth(`/orgs/${o.org_id}/trusts`),
            fetchWithAuth(`/orgs/${o.org_id}/activity?limit=50`),
          ]);
          if (mRes.ok) mem[o.org_id] = await mRes.json();
          if (tRes.ok) trs[o.org_id] = (await tRes.json()).trusts || [];
          if (aRes.ok) acts[o.org_id] = (await aRes.json()).events || [];
          if (!mRes.ok || !tRes.ok || !aRes.ok) failed[o.org_id] = true;
        } catch { failed[o.org_id] = true; }
      }));
      setMembers(mem); setTrustsByOrg(trs); setActivity(acts); setFailedOrgs(failed);
      setLastUpdated(new Date());
    } catch (e) {
      setErr(e);
      showError(toast, e, { page: 'OrgConsole' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ——— Phase 0: server-side search (fires when the focused org is big enough) ———
  const trustCount = (trustsByOrg[focusOrg?.org_id] || []).filter(Boolean).length;
  const serverMode = trustCount > SERVER_MODE_THRESHOLD;
  useEffect(() => {
    if (!serverMode || !focusOrg) { setServerTrusts(null); return; }
    const params = {
      q: trustQuery, level: trustLevel, status: trustStatus,
      deadline: trustDeadline, sort: trustSort,
      page: String(serverPage), page_size: String(TRUST_PAGE_SIZE),
    };
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const qs = new URLSearchParams(params).toString();
        const res = await fetchWithAuth(`/orgs/${focusOrg.org_id}/trusts/search?${qs}`);
        if (res.ok) {
          const body = await res.json();
          setServerTrusts(body.trusts || []);
          setServerTotal(body.total || 0);
        }
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [serverMode, focusOrg?.org_id, trustQuery, trustLevel, trustStatus, trustDeadline, trustSort, serverPage]);

  // ——— Phase 1: dashboard + queue fetch (any active member) ———
  const loadOverview = useCallback(async (orgId) => {
    try {
      const [ov, q] = await Promise.all([
        fetchWithAuth(`/orgs/${orgId}/overview`),
        fetchWithAuth(`/org/queue?org_id=${orgId}&limit=50`),
      ]);
      if (ov.ok) {
        const body = await ov.json();
        setOverview(prev => ({ ...prev, [orgId]: body }));
      }
      if (q.ok) setQueue(await q.json());
    } catch { /* sections degrade silently to base console */ }
  }, []);
  useEffect(() => { if (focusOrg) loadOverview(focusOrg.org_id); }, [focusOrg?.org_id, loadOverview]);

  const loadCalendar = useCallback(async (orgId) => {
    try {
      const res = await fetchWithAuth(`/orgs/${orgId}/calendar`);
      if (res.ok) setFirmCal(await res.json());
    } catch { /* calendar degrades out */ }
  }, []);
  useEffect(() => { if (focusOrg) loadCalendar(focusOrg.org_id); }, [focusOrg?.org_id, loadCalendar]);

  // URL sync — shareable/stable filtered views (?q=&level=&status=...)
  useEffect(() => {
    const sp = new URLSearchParams();
    if (trustQuery) sp.set('q', trustQuery);
    if (trustLevel !== 'all') sp.set('level', trustLevel);
    if (trustStatus !== 'all') sp.set('status', trustStatus);
    if (trustDeadline !== 'all') sp.set('deadline', trustDeadline);
    if (trustSort !== 'pending_desc') sp.set('sort', trustSort);
    setSearchParams(sp, { replace: true });
  }, [trustQuery, trustLevel, trustStatus, trustDeadline, trustSort, setSearchParams]);

  // my role in the focused org (owner vs member — gates admin actions)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!focusOrg) { if (alive) setIsOwner(false); return; }
      try {
        const me = await fetchWithAuth('/auth/me');
        if (!me.ok) return;
        const profile = await me.json();
        if (!alive) return;
        const roster = members[focusOrg.org_id] || [];
        const mine = roster.find(m => (m.email || '').toLowerCase() === (profile.email || '').toLowerCase());
        setIsOwner(mine?.role === 'owner');
      } catch { /* role probe optional */ }
    })();
    return () => { alive = false; };
  }, [focusOrg, members]);

  // Deep-link (Option B, 2026-10-01): workspace entry is an explicit, audited
  // "viewing" session — same machinery as admin impersonation. The ENTER
  // endpoint validates the grant server-side (level/expiry/revocation) and
  // writes the audit row; org_view_data drives OrgViewBanner + Exit.
  // ——— enter-confirmation modal (council R1+R3, 2026-10-09) ———
  // Entry into a client's workspace is gated behind an explicit confirm step:
  // the impersonation framing must be unmistakable BEFORE the click happens.
  const [confirmEntry, setConfirmEntry] = useState(null); // {trust, route}

  const goToTrustSection = async (trust, route) => {
    try {
      const res = await fetchWithAuth(
        `/orgs/enter-trust/${trust.trust_id}`,
        { method: 'POST' },
      );
      if (!res.ok) throw new Error(`enter-trust ${res.status}`);
      const data = await res.json();
      setSelectedTrust({ trust_id: trust.trust_id, name: trust.name });
      sessionStorage.setItem('org_view_data', JSON.stringify({
        trust_id: data.trust.trust_id,
        trust_name: data.trust.name,
        client_name: data.client.name || data.client.email,
        org_id: data.org.org_id,
        view_level: data.view_level,
        expires_at: data.expires_at,
        entered_at: data.entered_at,
        return_path: data.return_path,
      }));
      window.dispatchEvent(new Event('org_view_changed'));
      navigate(data.return_path || route);
    } catch (e) {
      if (e && e.status === 403) {
        toast.error('Your access to this trust is not active — ask the client to re-grant or check the expiry.');
      } else {
        showError(toast, e, { page: 'OrgConsole' });
      }
    }
  };

  // ——— derived: needs-attention rail (computed over ALL trusts of focus org) ———
  const rail = useMemo(() => {
    const ts = (trustsByOrg[focusOrg?.org_id] || []).filter(Boolean);
    const pending = ts.reduce((n, t) => n + (t.pending_minutes || 0), 0);
    const week = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    const deadlines = ts.filter(t => t.next_deadline && t.next_deadline.slice(0, 10) <= week).length;
    return { pending, deadlineTrs: deadlines, trusts: ts.length };
  }, [trustsByOrg, focusOrg]);

  // ——— derived: searchable/filtered + paged trusts ———
  const filteredTrusts = useMemo(() => {
    const ts = (trustsByOrg[focusOrg?.org_id] || []).filter(Boolean);
    const q = trustQuery.trim().toLowerCase();
    let out = ts.filter(t => {
      if (trustLevel !== 'all' && (t.grant_level || 'viewer') !== trustLevel) return false;
      if (!q) return true;
      return [t.name, t.owner_name, t.owner_email, t.grantor_name, t.trustee_name]
        .some(v => (v || '').toLowerCase().includes(q));
    });
    // pending-first so the work surfaces even in a long list
    out = [...out].sort((a, b) => (b.pending_minutes || 0) - (a.pending_minutes || 0));
    return out;
  }, [trustsByOrg, focusOrg, trustQuery, trustLevel]);
  const visibleTrusts = filteredTrusts.slice(0, trustShown);

  // ——— derived: CLIENT-first roster (council R1+R2, 2026-10-09) ———
  // Server-mode variant: trusts arrive as a page array, not trustsByOrg.
  const serverRoster = (ts) => {
    const byClient = new Map();
    for (const t of ts.filter(Boolean)) {
      const key = t.owner_user_id || (t.owner_name ? `__by_name__${t.owner_name}` : `__unclaimed__${t.trust_id}`);
      if (!byClient.has(key)) byClient.set(key, { key, name: t.owner_name || 'Awaiting client account', email: t.owner_email || '', trusts: [] });
      byClient.get(key).trusts.push(t);
    }
    return [...byClient.values()].sort((a, b) => {
      const au = a.key.startsWith('__unclaimed__'), bu = b.key.startsWith('__unclaimed__');
      if (au !== bu) return au ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // ——— derived: CLIENT-first roster (council R1+R2, 2026-10-09) ———
  // Advisors think in clients, not securities: one card per client person,
  // their trust(s) nested as rows. Trusts without an owner are unclaimed and
  // group under a synthetic 'awaiting' bucket so they stay visible and honest.
  const clientRoster = useMemo(() => {
    const ts = filteredTrusts;
    const byClient = new Map();
    for (const t of ts) {
      const key = t.owner_user_id || (t.owner_name ? `__by_name__${t.owner_name}` : `__unclaimed__${t.trust_id}`);
      if (!byClient.has(key)) byClient.set(key, { key, name: t.owner_name || 'Awaiting client account', email: t.owner_email || '', trusts: [] });
      byClient.get(key).trusts.push(t);
    }
    // named clients first, then unclaimed; alphabetical within groups
    return [...byClient.values()].sort((a, b) => {
      const au = a.key.startsWith('__unclaimed__'), bu = b.key.startsWith('__unclaimed__');
      if (au !== bu) return au ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [filteredTrusts, focusOrg]);


  // ——— derived: member list (search, sorted owner→active→invited) ———
  const roster = useMemo(() => {
    const all = members[focusOrg?.org_id] || [];
    const q = teamQuery.trim().toLowerCase();
    const rank = { owner: 0, admin: 1, member: 2 };
    return all.filter(m => !q || `${m.name || ''} ${m.email || ''}`.toLowerCase().includes(q))
      .sort((a, b) => (rank[a.role] ?? 3) - (rank[b.role] ?? 3));
  }, [members, focusOrg, teamQuery]);

  const countsByStatus = useMemo(() => {
    const all = members[focusOrg?.org_id] || [];
    return {
      active: all.filter(m => m.status === 'active').length,
      invited: all.filter(m => m.status === 'invited').length,
      suspended: all.filter(m => m.status === 'suspended').length,
    };
  }, [members, focusOrg]);

  // ——— member actions (real backend: PATCH status/role) ———
  // Confirmable member actions confirm through an AlertDialog (UI standard — the
  // browser prompt is retired): the menu click PARKS the action here, and the
  // dialog's Confirm button resumes it with the exact same body/copy.
  const patchMember = (member, body, confirmLabel) => {
    setConfirmMember({ member, body, confirmLabel });
  };

  const performPatchMember = async (member, body) => {
    try {
      const res = await fetchWithAuth(
        `/orgs/${focusOrg.org_id}/members/${member.member_id}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const det = await res.json().catch(() => ({}));
        toast.error(det?.detail?.code === 'owner_role_transfer_not_supported'
          ? 'Ownership transfer is not supported here yet.'
          : 'Could not update that member.');
        return;
      }
      toast.success('Member updated.');
      await load();
    } catch (e) { showError(toast, e, { page: 'OrgConsole' }); }
  };

  // ——— invite ———
  const sendInvite = async () => {
    if (!inviteOrg || !inviteEmail.trim()) return;
    // Pre-submit guards (TICKET-20261001-CLIENT-GUARDS): bad email format and
    // "already on the roster" stop at the dialog — the backend rejects both,
    // but every rejected submit was landing in the error log as 422 noise.
    const email = inviteEmail.trim();
    if (!isValidEmail(email)) {
      setInviteErr('Enter a valid email address to invite.');
      return;
    }
    const alreadyOnRoster = (members[inviteOrg.org_id] || []).some(
      m => (m.email || '').toLowerCase() === email.toLowerCase()
    );
    if (alreadyOnRoster) {
      setInviteErr('That email is already on this org\'s roster.');
      return;
    }
    setInviting(true); setInviteErr('');
    try {
      const res = await fetchWithAuth(`/orgs/${inviteOrg.org_id}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      if (res.ok) {
        toast.success(`Invite sent to ${inviteEmail.trim()}.`);
        setInviteOpen(false); setInviteEmail(''); setInviteRole('member');
        await load();
      } else {
        const body = await res.json().catch(() => ({}));
        const code = body?.detail?.code || body?.detail;
        setInviteErr(
          body?.detail?.message
          || humanizeErrorCode(typeof code === 'string' ? code : null)
          || 'Invite failed. Check the email and try again.'
        );
      }
    } catch (e) {
      setInviteErr('Network error — try again.');
      showError(toast, e, { page: 'OrgConsole' });
    } finally { setInviting(false); }
  };

  const OrgRouteEmptyState = ({ onRetry }) => (
    <div className="card-trust text-center py-12">
      <Building2 className="w-12 h-12 text-navy/30 mx-auto mb-4" />
      <h2 className="font-serif text-xl text-navy mb-2">No organization yet</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
        If someone invited you to their organization, accept the email invitation first — that's the
        fastest path in. Or, if you run your own practice, create your organization here.
      </p>
      <Button
        variant="outline" className="btn-secondary"
        onClick={async () => {
          const name = window.prompt('Name your organization (e.g., "Barlow Fiduciary Group")');
          if (!name || !name.trim()) return;
          try {
            const res = await fetchWithAuth('/orgs', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: name.trim() }),
            });
            if (res.ok) { toast.success('Organization created.'); await onRetry(); }
            else toast.error('Could not create org — it may already exist.');
          } catch (e2) { showError(toast, e2, { page: 'OrgConsole' }); }
        }}
        data-testid="create-org-btn"
      >
        <Building2 className="w-4 h-4 mr-2" /> Create Organization
      </Button>
    </div>
  );

  // Member-side view: an org member with no granted trusts sees what happened,
  // who can fix it, and what happens next — instead of an empty list.
  const GrantedTrustsEmptyState = () => (
    <div className="card-trust text-center py-10 mb-4" data-testid="no-granted-trusts">
      <FileText className="w-10 h-10 text-navy/30 mx-auto mb-3" />
      <h3 className="font-serif text-md text-navy mb-1">Nothing shared with you yet</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        The organization owner hasn't granted you access to any client trusts yet. Once they do, those trusts appear
        here and you'll see exactly what you can work on. Until then, nothing is needed from you.
      </p>
    </div>
  );

  const grantLevelBadge = (level) => (
    <Badge
      data-testid="trust-card-level"
      variant={level === 'preparer' ? 'default' : 'secondary'}
      className={`capitalize text-xs ${level === 'preparer' ? 'bg-navy text-white hover:opacity-90' : 'text-navy'}`}
    >
      {level}
    </Badge>
  );

  const clientCard = (c) => {
    const trusts = c.trusts || [];
    const claimed = trusts.filter(t => t.owner_user_id);
    const pending = trusts.reduce((n, t) => n + (t.pending_minutes || 0), 0);
    const nextDeadline = trusts.map(t => t.next_deadline).filter(Boolean).sort()[0];
    const unclaimedOnly = claimed.length === 0 && !c.key.startsWith('__by_name__');
    const primaryTrust = claimed[0];
    const multi = claimed.length > 1;
    return (
      <Card key={c.key} className="card-trust" data-testid={unclaimedOnly ? 'trust-card-unclaimed-group' : 'client-card'}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <h3 className="font-serif text-base text-navy truncate" title={c.email || c.name}>
                {unclaimedOnly ? 'Awaiting client account' : c.name}
              </h3>
              {unclaimedOnly ? (
                <p className="text-xs text-muted-foreground italic" data-testid="trust-card-unclaimed">
                  Access was attested, not yet live — the trust connects once the client signs up.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground truncate" data-testid="trust-card-client">{c.email}</p>
              )}
            </div>
            {claimed[0] ? grantLevelBadge(claimed[0].grant_level) : null}
          </div>

          {/* trusts nested under the client */}
          <ul className="mb-4 space-y-1.5">
            {trusts.map(t => (
              <li key={t.trust_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-navy truncate" title={t.name}>{t.name || 'Untitled trust'}</span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
                  {t.pending_minutes > 0 ? (
                    <span className="inline-flex items-center gap-1 font-medium text-navy bg-gold/20 border border-gold/40 rounded-full px-2 py-0.5" data-testid="trust-card-pending">
                      <FileSignature className="w-3 h-3 text-gold" />
                      {t.pending_minutes} pending
                    </span>
                  ) : null}
                  {t.next_deadline ? <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3 text-gold" />{fmtDate(t.next_deadline)}</span> : null}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-xs text-muted-foreground mb-3">Scoped, time-limited, revocable by the client.</p>

          <div className="flex flex-col gap-2">
            {!unclaimedOnly && primaryTrust ? (
              <Button
                className="btn-primary w-full"
                data-testid={`enter-account-${primaryTrust.trust_id}`}
                onClick={() => setConfirmEntry({ trust: primaryTrust, route: '/minutes' })}
              >
                <Eye className="w-4 h-4 mr-2" />
                Enter {multi ? 'client' : `${c.name.split(' ')[0]}'s`} account
                <ArrowUpRight className="w-4 h-4 ml-auto" />
              </Button>
            ) : (
              <Button className="btn-secondary w-full" disabled data-testid="awaiting-client-btn">
                Awaiting client account
              </Button>
            )}
            {pending > 0 && primaryTrust ? (
              <Button
                variant="ghost" size="sm"
                className="text-xs text-muted-foreground hover:text-navy"
                data-testid={`review-minutes-${primaryTrust.trust_id}`}
                onClick={() => setConfirmEntry({ trust: primaryTrust, route: '/minutes' })}
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Review {pending} pending {pending === 1 ? 'minute' : 'minutes'}
              </Button>
            ) : null}
            {primaryTrust ? (
              <div className="flex gap-2 pt-1 border-t border-border/40">
                <Button
                  variant="ghost" size="sm"
                  className="text-xs text-muted-foreground hover:text-navy flex-1"
                  onClick={async () => {
                    try {
                      const res = await fetchWithAuth(`/exports/defense-summary/${primaryTrust.trust_id}`);
                      if (!res.ok) { toast.error('Could not build the summary.'); return; }
                      const blob = await res.blob();
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `defense-summary-${primaryTrust.trust_id}.pdf`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    } catch { toast.error('Could not build the summary.'); }
                  }}
                  data-testid={`defense-summary-${primaryTrust.trust_id}`}
                >
                  Defense Summary
                </Button>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    );
  };

  const SectionHeader = ({ icon: Ic, title, count, right, testid }) => (
    <div className="flex items-center justify-between mb-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Ic className="w-4 h-4 text-navy" />
        <h3 className="font-medium text-navy">{title}{count != null ? ` (${count})` : ''}</h3>
      </div>
      {right}
    </div>
  );

  const activityFeed = (o) => {
    const events = (activity[o.org_id] || []).filter(Boolean);
    return (
      <Card className="card-trust" data-testid="activity-feed">
        <CardContent className="pt-6">
          <SectionHeader
            icon={Activity} title="Activity"
            right={<span className="text-xs text-muted-foreground">{events.length ? `${events.length} recent` : ''}</span>}
          />
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No activity yet. Actions org members take on the left appear here — who did what, on whose behalf.
            </p>
          ) : (
            <>
            <ul className="space-y-2 md:max-h-64 md:overflow-y-auto md:pr-1">
              {events.filter(ev => ev && (ev.action || ev.activity_type)).slice(0, 12).map(ev => {
                // 2026-10-09: every row names the CLIENT it's about — the feed's job is
                // 'who did what, for which client, when'. Trust→client via the roster.
                // Defensively handle raw lifecycle rows (old backend, pre-normalization):
                const action0 = ev.action || ev.activity_type || '';
                const t = (trustsByOrg[o.org_id] || []).find(x => x.trust_id === ev.trust_id);
                const who = ev.member_name || (action0.startsWith('minutes_') || action0.startsWith('distribution_') || action0.startsWith('schedule_a_') || action0 === 'kit_generated' ? 'The firm' : 'The client');
                return (
                <li key={ev.event_id || ev.created_at} className="text-sm border-b last:border-0 border-border/50 pb-2 last:pb-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-navy min-w-0 truncate">
                      {ACTION_LABELS[action0] || action0}
                      {t?.owner_name ? <span className="text-muted-foreground"> — {t.owner_name}</span> : (t?.name ? <span className="text-muted-foreground"> — {t.name}</span> : null)}
                    </p>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtRelative(ev.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {who}{ev.attribution ? ` · ${String(ev.attribution).replace(/\(expires (\d{4}-\d{2}-\d{2})\)/g, (_, d) => `(expires ${fmtShort(d)})`)}` : ''}
                  </p>
                </li>
                );
              })}
            </ul>
            {events.length > 12 ? (
              <p className="text-xs text-muted-foreground mt-3">Showing the 12 most recent of {events.length}.</p>
            ) : null}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const memberRow = (m) => (
    <div key={m.member_id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0 border-border/50 text-sm min-h-[36px]">
      <span className="min-w-0 truncate text-muted-foreground flex-1" title={m.email || m.name}>
        {m.name || m.email}
        {m.name && m.email ? <span className="text-xs text-muted-foreground/70"> · {m.email}</span> : null}
      </span>
      <span className="flex items-center gap-2 shrink-0">
        <span className={`text-xs ${m.status === 'active' ? 'text-muted-foreground' : 'text-gold'}`}>
          {m.status === 'active' ? 'Active' : m.status === 'suspended' ? 'Suspended' : 'Invited'}
        </span>
        <Badge variant={m.role === 'owner' ? 'default' : 'secondary'} className="capitalize text-xs pointer-events-none opacity-90">{m.role}</Badge>
        {m.role !== 'owner' && isOwner ? (
          <Button
            variant="ghost" size="sm" className="h-8 w-8 p-0"
            aria-label={`Member actions for ${m.name || m.email}`}
            data-testid={`member-menu-${m.member_id}`}
            onClick={() => setMenuMember(menuMember === m.member_id ? null : m.member_id)}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        ) : null}
      </span>
    </div>
  );

  const memberMenu = (m) => (
    <div className="flex flex-wrap gap-2 py-2 pl-2 bg-cream/40 rounded" data-testid={`member-menu-open-${m.member_id}`}>
      {m.status === 'invited' ? (
        <Button variant="outline" size="sm" className="btn-secondary" onClick={() => patchMember(m, { status: 'active' }, `Mark ${m.email} as accepted (they can sign in)?`)}>
          <PlayCircle className="w-4 h-4 mr-1" /> Mark accepted
        </Button>
      ) : m.status === 'active' ? (
        <Button variant="outline" size="sm" className="btn-secondary" onClick={() => patchMember(m, { status: 'suspended' }, `Suspend ${m.name || m.email}? They lose org access until reinstated.`)}>
          <PauseCircle className="w-4 h-4 mr-1" /> Suspend
        </Button>
      ) : (
        <Button variant="outline" size="sm" className="btn-secondary" onClick={() => patchMember(m, { status: 'active' }, `Reinstate ${m.name || m.email}?`)}>
          <PlayCircle className="w-4 h-4 mr-1" /> Reinstate
        </Button>
      )}
      {isOwner && m.role !== 'owner' && m.role !== 'admin' ? (
        <Button variant="outline" size="sm" className="btn-secondary" onClick={() => patchMember(m, { role: 'admin' }, `Make ${m.name || m.email} an org admin?`)}>
          Make admin
        </Button>
      ) : null}
      {isOwner && m.role === 'admin' ? (
        <Button variant="outline" size="sm" className="btn-secondary" onClick={() => patchMember(m, { role: 'member' }, `Set ${m.name || m.email} back to member?`)}>
          Set to member
        </Button>
      ) : null}
    </div>
  );

  const teamSection = (o) => {
    const all = members[o.org_id] || [];
    const shown = teamOpen ? roster : roster.slice(0, 5);
    return (
      <Card className="card-trust mb-4" data-testid="team-card">
        <CardContent className="pt-6">
          <SectionHeader
            icon={Users} title="Team" count={all.length}
            right={
              <div className="flex items-center gap-2">
                {roster.length > 8 || all.length > 8 ? (
                  <Button variant="ghost" size="sm" className="h-8" data-testid="team-toggle" onClick={() => setTeamOpen(v => !v)}>
                    {teamOpen ? 'Show less' : `Show all ${all.length}`}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${teamOpen ? 'rotate-180' : ''}`} />
                  </Button>
                ) : null}
                <Button variant="outline" size="sm" className="btn-secondary"
                  onClick={() => { setInviteOrg(o); setInviteOpen(true); }}
                  data-testid={`invite-${o.org_id}`}
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Invite
                </Button>
              </div>
            }
          />
          {all.length > 8 ? (
            <Input
              value={teamQuery} onChange={e => setTeamQuery(e.target.value)}
              placeholder="Search team…" className="mb-3 text-sm" data-testid="team-search"
            />
          ) : null}
          <div className="text-xs text-muted-foreground mb-2">
            {countsByStatus.active} active{countsByStatus.invited ? ` · ${countsByStatus.invited} invited` : ''}{countsByStatus.suspended ? ` · ${countsByStatus.suspended} suspended` : ''}
          </div>
          {shown.map(m => (
            <div key={`row-${m.member_id}`}>
              {memberRow(m)}
              {menuMember === m.member_id ? memberMenu(m) : null}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  const orgSwitcher = () => {
    if (orgs.length === 0) return null;
    const f = focusOrg || orgs[0];
    const orgStats = (o) => {
      const ts = (trustsByOrg[o.org_id] || []).filter(Boolean);
      const pend = ts.reduce((n, t) => n + (t.pending_minutes || 0), 0);
      const mm = (members[o.org_id] || []).length;
      return `${mm} member${mm === 1 ? '' : 's'} · ${ts.length} trust${ts.length === 1 ? '' : 's'}${pend ? ` · ${pend} pending` : ''}`;
    };
    if (orgs.length <= 3) {
      return (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="org-switcher">
          {orgs.map(o => (
            <Button
              key={o.org_id}
              variant="ghost"
              onClick={() => { setFocusOrg(o); setTrustQuery(''); setTrustLevel('all'); setTrustShown(TRUST_PAGE_SIZE); setTeamOpen(false); setTeamQuery(''); }}
              className={`px-3 py-1.5 text-sm border transition-colors ${o.org_id === f.org_id
                ? 'bg-navy text-white border-navy hover:bg-navy hover:text-white'
                : 'bg-transparent text-navy border-border hover:border-navy/50 hover:bg-transparent hover:text-navy'}`}
              data-testid={`org-chip-${o.org_id}`}
            >
              {o.name}
              <span className={`ml-2 text-xs ${o.org_id === f.org_id ? 'text-white/70' : 'text-muted-foreground'}`}>
                {orgStats(o)}
              </span>
            </Button>
          ))}
        </div>
      );
    }
    // many orgs → dropdown
    return (
      <div className="relative mb-4" data-testid="org-switcher">
        <Button variant="outline" className="btn-secondary" data-testid="org-switcher-toggle" onClick={() => setSwitcherOpen(v => !v)}>
          <Building2 className="w-4 h-4 mr-2" /> {f.name} <span className="text-xs text-muted-foreground ml-2">{orgStats(f)}</span>
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} />
        </Button>
        {switcherOpen ? (
          <div className="absolute z-20 mt-2 w-full max-w-sm card-trust p-2" data-testid="org-switcher-menu">
            {orgs.map(o => (
              <Button key={o.org_id}
                variant="ghost"
                onClick={() => { setFocusOrg(o); setSwitcherOpen(false); setTrustQuery(''); setTrustLevel('all'); setTrustShown(TRUST_PAGE_SIZE); setTeamOpen(false); setTeamQuery(''); }}
                className="w-full justify-start text-left px-3 py-2 rounded hover:bg-cream/60 text-sm"
              >
                <span className="text-navy">{o.name}</span>
                <span className="text-xs text-muted-foreground ml-2">{orgStats(o)}</span>
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="main-layout" data-testid="org-console-page">
      <Sidebar />
      <main className="main-content dot-grid">
        <div className="page-container">
          <PageHeader
            title="Org Console"
            subtitle="Your practice's account: the client trusts shared with your organization, the team that works them, and what the team does."
            help={{
              items: [
                { text: 'This console lists every client trust shared with your organization and who on your team can work it' },
                { text: 'Switch organizations with the selector below the title; the level filter appears once your trust book grows' },
                { text: 'Manage teammates from the Team card and export a governance packet from the org settings row' },
              ],
              taPrompt: 'Walk me through the Org Console',
            }}
            actions={
              <Button variant="outline" className="btn-secondary" onClick={load} disabled={loading} data-testid="refresh-btn" ref={refreshBtn}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            }
          />
          {lastUpdated ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="last-updated">
              Updated {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </p>
          ) : null}

          {loading && !orgs.length ? (
            <div className="card-trust skeleton h-40 w-full" />
          ) : err ? (
            <div className="card-trust text-center py-10" data-testid="org-console-error">
              <AlertTriangle className="w-10 h-10 text-gold mx-auto mb-3" />
              <h2 className="font-serif text-xl text-navy mb-2">Couldn't load your organizations</h2>
              <p className="text-sm text-muted-foreground mb-4">Check your connection and try again — your data is safe.</p>
              <Button className="btn-primary" onClick={load} data-testid="org-console-retry">Try again</Button>
            </div>
          ) : orgs.length === 0 ? (
            <OrgRouteEmptyState onRetry={load} />
          ) : (
            <>
              {orgSwitcher()}
              {failedOrgs[focusOrg?.org_id] ? (
                <div className="card-trust mb-4 flex items-center justify-between" data-testid="org-section-failed">
                  <span className="text-sm text-navy">Some sections for {focusOrg.name} didn't load.</span>
                  <Button variant="outline" size="sm" className="btn-secondary" onClick={load}>Retry</Button>
                </div>
              ) : null}
              {focusOrg ? (
                <div data-testid={`org-card-${focusOrg.org_id}`}>
                  {/* needs-attention rail */}
                  {(rail.pending > 0 || rail.deadlineTrs > 0) ? (
                    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="attention-rail">
                      {rail.pending > 0 ? (
                        <Button
                          onClick={() => { setTrustQuery(''); setTrustLevel('all'); }}
                          className="h-auto inline-flex items-center gap-1.5 text-xs font-medium text-navy bg-gold/20 border border-gold/40 hover:bg-gold/25 hover:text-navy px-3 py-1.5"
                          data-testid="rail-pending"
                        >
                          <FileSignature className="w-3.5 h-3.5 text-gold" />
                          {rail.pending} pending {rail.pending === 1 ? 'minute' : 'minutes'} across your trusts
                        </Button>
                      ) : null}
                      {rail.deadlineTrs > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-navy border border-gold/40 rounded-full px-3 py-1.5" data-testid="rail-deadlines">
                          <CalendarClock className="w-3.5 h-3.5 text-gold" />
                          {rail.deadlineTrs} {rail.deadlineTrs === 1 ? 'deadline' : 'deadlines'} within 7 days
                        </span>
                      ) : null}
                      <span className="text-xs text-muted-foreground">sorted pending-first below</span>
                    </div>
                  ) : null}

                  {/* overview + review queue (Phase 1) */}
                  {overview[focusOrg.org_id] ? (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="portfolio-overview">
                      {[
                        { label: 'Portfolio health', value: overview[focusOrg.org_id].portfolio?.average_health ?? '—', testid: 'ov-health' },
                        { label: 'At-risk trusts', value: overview[focusOrg.org_id].portfolio?.at_risk ?? 0, testid: 'ov-at-risk' },
                        { label: 'Overdue tasks', value: overview[focusOrg.org_id].portfolio?.overdue_total ?? 0, testid: 'ov-overdue' },
                        { label: 'Minutes pending', value: overview[focusOrg.org_id].portfolio?.pending_minutes_total ?? 0, testid: 'ov-pending' },
                        { label: 'Due this week', value: overview[focusOrg.org_id].portfolio?.due_week ?? 0, testid: 'ov-due-week' },
                      ].map(cell => (
                        <div key={cell.label} className="card-trust text-center py-3">
                          <p className="font-serif text-xl text-navy" data-testid={cell.testid}>{cell.value}</p>
                          <p className="text-xs text-muted-foreground">{cell.label}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {/* trusts */}
                  <div className="mb-2 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-navy" />
                      <h3 className="font-medium text-navy" data-testid="trusts-heading">
                        Client trusts ({(trustsByOrg[focusOrg.org_id] || []).filter(Boolean).length})
                      </h3>
                      {serverMode && serverTrusts ? (
                        <span className="text-xs text-muted-foreground" data-testid="trust-result-count">
                          {serverTotal === 0 ? 'no matches' : `showing ${serverTrusts.length} of ${serverTotal}`}
                        </span>
                      ) : null}
                    </div>
                    {trustCount > 6 ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-2.5" />
                          <Input
                            value={trustQuery} onChange={e => { setTrustQuery(e.target.value); setServerPage(1); }}
                            placeholder="Search trust, client, or email…" className="pl-8 text-sm w-64"
                            data-testid="trust-search"
                          />
                        </div>
                        <div className="flex gap-1">
                          {['all', 'preparer', 'viewer'].map(lv => (
                            <Button key={lv}
                              variant="ghost"
                              onClick={() => { setTrustLevel(lv); setServerPage(1); }}
                              className={`px-2.5 py-1 h-auto text-xs border transition-colors ${trustLevel === lv ? 'bg-navy text-white border-navy hover:bg-navy hover:text-white' : 'bg-transparent text-navy border-border hover:border-navy/50 hover:bg-transparent hover:text-navy'}`}
                              data-testid={`trust-level-${lv}`}
                            >
                              {lv === 'all' ? 'All levels' : lv}
                            </Button>
                          ))}
                        </div>
                        {serverMode ? (
                          <div className="flex gap-1" data-testid="advanced-filters">
                            <select
                              value={trustStatus} onChange={e => { setTrustStatus(e.target.value); setServerPage(1); }}
                              className="text-xs border border-border bg-background px-2 py-1.5 text-navy"
                              data-testid="trust-status-filter"
                              aria-label="Status filter"
                            >
                              <option value="all">All status</option>
                              <option value="attention">Needs attention</option>
                              <option value="healthy">Healthy</option>
                            </select>
                            <select
                              value={trustDeadline} onChange={e => { setTrustDeadline(e.target.value); setServerPage(1); }}
                              className="text-xs border border-border bg-background px-2 py-1.5 text-navy"
                              data-testid="trust-deadline-filter"
                              aria-label="Deadline filter"
                            >
                              <option value="all">Any deadline</option>
                              <option value="overdue">Overdue</option>
                              <option value="7d">Next 7 days</option>
                              <option value="30d">Next 30 days</option>
                              <option value="quarter">This quarter</option>
                              <option value="none">No deadline</option>
                            </select>
                            <select
                              value={trustSort} onChange={e => { setTrustSort(e.target.value); setServerPage(1); }}
                              className="text-xs border border-border bg-background px-2 py-1.5 text-navy"
                              data-testid="trust-sort"
                              aria-label="Sort"
                            >
                              <option value="pending_desc">Most pending first</option>
                              <option value="deadline_asc">Deadline soonest</option>
                              <option value="health_asc">Lowest score</option>
                              <option value="name_asc">Name A–Z</option>
                            </select>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Scoped, time-limited, revocable by the client.</p>
                  {(trustsByOrg[focusOrg.org_id] || []).length === 0 ? (
                    <GrantedTrustsEmptyState />
                  ) : serverMode && (serverTrusts || []).length === 0 ? (
                    <div className="card-trust text-center py-8 mb-4" data-testid="trust-filter-empty">
                      <p className="text-sm text-muted-foreground mb-2">
                        No trusts match {trustQuery ? `"${trustQuery}"` : 'these filters'}
                        {trustStatus !== 'all' || trustDeadline !== 'all' ? ' + filter selections' : ''}.
                      </p>
                      <Button variant="outline" size="sm" className="btn-secondary" onClick={() => { setTrustQuery(''); setTrustLevel('all'); setTrustStatus('all'); setTrustDeadline('all'); setServerPage(1); }}>Clear</Button>
                    </div>
                  ) : !serverMode && filteredTrusts.length === 0 ? (
                    <div className="card-trust text-center py-8 mb-4" data-testid="trust-filter-empty">
                      <p className="text-sm text-muted-foreground mb-2">No trusts match “{trustQuery || trustLevel}”.</p>
                      <Button variant="outline" size="sm" className="btn-secondary" onClick={() => { setTrustQuery(''); setTrustLevel('all'); }}>Clear</Button>
                    </div>
                  ) : (
                    <>
                      {serverMode ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4" data-testid="server-trust-grid">
                          {serverRoster(serverTrusts || []).map(c => clientCard(c))}
                          {searching ? <p className="text-xs text-muted-foreground">Searching…</p> : null}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
                          {clientRoster.map(c => clientCard(c))}
                        </div>
                      )}
                      {serverMode && serverTotal > serverPage * TRUST_PAGE_SIZE ? (
                        <Button variant="outline" className="btn-secondary w-full mb-4" onClick={() => setServerPage(n => n + 1)} data-testid="trust-show-more">
                          Load page {serverPage + 1} of {Math.ceil(serverTotal / TRUST_PAGE_SIZE)}
                        </Button>
                      ) : null}
                      {!serverMode && filteredTrusts.length > trustShown ? (
                        <Button variant="outline" className="btn-secondary w-full mb-4" onClick={() => setTrustShown(n => n + TRUST_PAGE_SIZE)} data-testid="trust-show-more">
                          Show {Math.min(TRUST_PAGE_SIZE, filteredTrusts.length - trustShown)} more of {filteredTrusts.length}
                        </Button>
                      ) : null}
                    </>
                  )}

                  {/* Review queue + firm calendar: BELOW the client-trust selection (Jeff, 2026-10-09),
                       sharing a two-column row — queue left, calendar right (Jeff follow-up same day). */}
                  <div className="grid gap-4 md:grid-cols-2 items-start" data-testid="queue-calendar-row">
                  {queue && queue.items && queue.items.length > 0 ? (
                    <Card className="card-trust" data-testid="review-queue">
                      <CardContent className="pt-6">
                        <SectionHeader
                          icon={RefreshCw} title="Review Queue"
                          right={<span className="text-xs text-muted-foreground">
                            {queue.counts?.overdue ? `${queue.counts.overdue} overdue · ` : ''}{queue.counts?.due_week || 0} this week
                          </span>}
                        />
                        <ul className="space-y-2">
                          {queue.items.slice(0, 8).map(it => (
                            <li key={it.task_id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0 border-border/50 text-sm">
                              <div className="min-w-0">
                                <p className="text-navy truncate">{it.title || humanizeTaskType(it.task_type)}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {it.trust_name || it.trust_id} · due {it.due_date ? fmtShort(it.due_date) : '—'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {it.urgency === 'overdue' ? (
                                  <Badge className="bg-red-900 text-white text-xs">Overdue</Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">{it.urgency === 'due_week' ? 'This week' : 'Upcoming'}</Badge>
                                )}
                                {(it.grant_level === 'preparer') ? (
                                  <Button variant="outline" size="sm" className="btn-secondary"
                                    disabled={queueBusy === it.task_id}
                                    onClick={async () => {
                                      setQueueBusy(it.task_id);
                                      try {
                                        const res = await fetchWithAuth(`/org/queue/${it.task_id}/complete`, { method: 'POST' });
                                        if (res.ok) { toast.success('Task completed — next cycle scheduled.'); await load(); loadOverview(focusOrg.org_id); }
                                        else toast.error('Could not complete that task.');
                                      } finally { setQueueBusy(null); }
                                    }}
                                    data-testid={`queue-complete-${it.task_id}`}
                                  >
                                    {queueBusy === it.task_id ? '…' : 'Complete'}
                                  </Button>
                                ) : null}
                                <Button variant="outline" size="sm" className="btn-secondary"
                                  onClick={() => goToTrustSection({ trust_id: it.trust_id, name: it.trust_name }, '/tasks')}
                                  data-testid={`queue-open-${it.task_id}`}
                                >
                                  Open
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ) : null}
                  {firmCal && firmCal.weeks && firmCal.weeks.length > 0 ? (
                    <Card className="card-trust mb-4" data-testid="firm-calendar">
                      <CardContent className="pt-6">
                        <SectionHeader
                          icon={CalendarClock} title="Firm Calendar"
                          right={<span className="text-xs text-muted-foreground">{firmCal.counts?.total || 0} upcoming · next 90 days</span>}
                        />
                        <div className="space-y-3" data-testid="firm-calendar-body">
                          {firmCal.weeks.slice(0, 6).map(wk => (
                            <div key={wk.week} data-testid={`cal-week-${wk.week}`}>
                              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{weekLabel(wk.week)}</p>
                              <ul className="space-y-1.5">
                                {wk.items.map(it => {
                                  const chip = dueChip(it.due_date);
                                  return (
                                  <li key={it.task_id} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b last:border-0 border-border/40">
                                    <div className="min-w-0">
                                      <p className="text-navy truncate" title={it.title || it.task_type}>
                                        {it.title || humanizeTaskType(it.task_type)}
                                        {chip ? (
                                          <span className={`ml-2 inline-flex items-center text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${chip.cls}`} data-testid="due-chip">{chip.text}</span>
                                        ) : null}
                                        {it.automated ? (
                                          <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wide text-navy bg-gold/25 border border-gold/50 rounded px-1.5 py-0.5" data-testid="automated-badge">Automated</span>
                                        ) : null}
                                      </p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {it.trust_name || it.trust_id}{it.due_date ? ` · due ${fmtShort(it.due_date)}` : ''}
                                      </p>
                                    </div>
                                    <Button variant="outline" size="sm" className="btn-secondary shrink-0"
                                      onClick={() => goToTrustSection({ trust_id: it.trust_id, name: it.trust_name }, '/tasks')}
                                      data-testid={`cal-open-${it.task_id}`}
                                    >
                                      Open
                                    </Button>
                                  </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                  </div> {/* queue-calendar-row */}

                  {/* two-column zone on xl: trusts already full-width above; activity + team side by side on wide screens */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {activityFeed(focusOrg)}
                    {teamSection(focusOrg)}
                  </div>

                  {/* org settings row */}
                  <div className="card-trust mt-4 mb-8" data-testid="org-settings">
                    <CardContent className="pt-6 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-sm text-navy">
                        <Settings className="w-4 h-4 text-navy" />
                        <span className="font-medium">{focusOrg.name}</span>
                        <Button
                          variant="ghost" size="sm"
                          className="h-auto px-1 text-xs text-muted-foreground hover:text-navy hover:bg-transparent inline-flex items-center gap-1"
                          title={focusOrg.org_id}
                          onClick={() => { try { navigator.clipboard?.writeText(focusOrg.org_id); toast.success('Org ID copied.'); } catch {} }}
                          data-testid="org-id-copy"
                        >
                          <span>{focusOrg.org_id.slice(0, 10)}⋯{focusOrg.org_id.slice(-4)}</span>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="btn-secondary"
                          onClick={async () => {
                            try {
                              toast.info('Building the packet…');
                              const res = await fetchWithAuth(`/exports/org/${focusOrg.org_id}/packet`, { method: 'POST' });
                              if (!res.ok) { toast.error('Packet build failed.'); return; }
                              const blob = await res.blob();
                              const a = document.createElement('a');
                              a.href = URL.createObjectURL(blob);
                              a.download = 'governance-packet.zip';
                              a.click();
                              URL.revokeObjectURL(a.href);
                              toast.success('Governance packet downloaded.');
                            } catch { toast.error('Packet build failed.'); }
                          }}
                          data-testid="org-packet-btn"
                        >
                          Export Governance Packet
                        </Button>
                        <span className="text-xs text-muted-foreground">Renaming and org-level policy are coming — contact support for changes.</span>
                      </div>
                    </CardContent>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </main>

      {/* Confirm member action (AlertDialog per UI standard): the action
          parked by the member menu is resumed here; Cancel, Esc, or an
          outside click aborts it — the same no-op semantics as the old prompt. */}
      <AlertDialog open={!!confirmMember} onOpenChange={(open) => { if (!open) setConfirmMember(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{memberConfirmTitle(confirmMember)}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMember?.confirmLabel || 'Apply this change to this team member?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="member-confirm-confirm"
              className="btn-primary"
              onClick={(e) => {
                e.preventDefault();
                const parked = confirmMember;
                setConfirmMember(null);
                setMenuMember(null);
                if (parked?.member && parked?.body) performPatchMember(parked.member, parked.body);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enter-confirmation (council R1+R3, 2026-10-09): impersonation must be
          unmistakable BEFORE the click. Gating every entry path. */}
      <Dialog open={!!confirmEntry} onOpenChange={(o) => { if (!o) setConfirmEntry(null); }}>
        <DialogContent data-testid="enter-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmEntry && confirmEntry.trust.owner_name
                ? `Enter ${confirmEntry.trust.owner_name.split(' ')[0]}'s workspace?`
                : "Enter this client's workspace?"}
            </DialogTitle>
            <DialogDescription>
              You'll be acting inside {confirmEntry?.trust?.owner_name || 'the client'}'s TrustOffice
              account as {focusOrg?.name}, viewing {confirmEntry?.trust?.name}. Every action in this
              session is attributed to you and written to the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="enter-confirm-meta">
            {confirmEntry?.trust ? grantLevelBadge(confirmEntry.trust.grant_level) : null}
            <span>Scoped access — revocable by the client at any time.</span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="btn-secondary" onClick={() => setConfirmEntry(null)}>
              Cancel
            </Button>
            <Button
              className="btn-primary"
              data-testid="enter-confirm-go"
              onClick={() => {
                const payload = confirmEntry;
                setConfirmEntry(null);
                if (payload) goToTrustSection(payload.trust, payload.route);
              }}
            >
              <Eye className="w-4 h-4 mr-2" />
              Enter workspace
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a team member to {inviteOrg?.name}</DialogTitle>
            <DialogDescription>
              They'll get an email invite. Once they accept, they can open every trust this org has been
              granted — you can suspend or remove them anytime.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email" type="email" inputMode="email" autoCapitalize="none"
              placeholder="colleague@example.com" className="mt-1"
              value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !inviting && inviteEmail.trim()) sendInvite(); }}
              data-testid="invite-email-input"
            />
            <Label htmlFor="invite-role" className="mt-3">Role</Label>
            <select
              id="invite-role" value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="mt-1 w-full text-sm border border-border bg-background px-3 py-2"
              data-testid="invite-role-select"
            >
              <option value="member">Member</option>
              {isOwner ? <option value="admin">Admin</option> : null}
            </select>
            {inviteErr ? <p className="text-xs text-red-700 mt-2" data-testid="invite-error">{inviteErr}</p> : null}
          </div>
          <Button className="btn-primary w-full" onClick={sendInvite} disabled={inviting || !inviteEmail.trim()} data-testid="send-invite-btn">
            {inviting ? 'Sending…' : 'Send Invite'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
